import { BaseStore } from "./base-store";
import { AssignmentDemandRole } from "../enums/AssignmentDemandRole";
import { Employee } from "../models/Employee";
import { Absence } from "../models/Absence";
import { AssignmentDemand } from "../models/AssignmentDemand";
import { CalendarWeek } from "../models/CalendarWeek";
import { Project } from "../models/Project";
import { EmployeeUtilization } from "../models/EmployeeUtilization";
import { Skill } from "../models/Skill";
import { getEmployeeUtilizationByWeek, getLocalStorageWithExpiry, getWeekNumber, setLocalStorageWithExpiry } from "../common";
import { FirebaseApp } from 'firebase/app';
import * as _ from 'lodash';
import { ManuallyPlannedTime } from "../models/ManuallyPlannedTime";
import { AssignmentsHistory } from "../models/AssignmentsHistory";
import { CurrentUser } from "../models/CurrentUser";
import { AssignmentComment } from "../models/AssignmentComment";
import { Timestamp } from "firebase/firestore";
import { EmploymentPeriod } from "../models/EmploymentPeriod";
import dateTime from "../utils/date-time";
import { loadMockData } from "./mock-data";
import EventEmitter from "events";
import iso6391 from 'iso-639-1';
import { Language } from "../models/Language";
/**a
 * Create a new assignment Store
 */
/**
 * Represents a store for managing assignments, employees, projects, project roles, skill structure, locations, seniorities, and current user.
 * @extends BaseStore
 */
export class AssignmentStore extends BaseStore {

    employees = null;
    projects = null;
    projectRoles = null;
    skillStructure = null;
    locations = null;
    assignments = null;
    seniorities = null;
    currentUser: CurrentUser | null = null;
    currentDate = this.today;
    targetDate = this.today;
    timeWindowFrom = new Date(this.targetDate.getFullYear(), 6, 1);
    timeWindowTo = new Date(this.targetDate.getFullYear() + 1, 6, 0);
    firstDayOfCW1 = [1, 2, 3, 4, 5, 6, 7].find(day => { return getWeekNumber(new Date(this.targetDate.getFullYear(), 0, day)) === 1; });
    private subscribedAssignments: AssignmentDemand[];
    private assignmentChangeSubscribers: Function[] = [];

    /** @type {Map<string, Employee> | null} */
    allEmployeesMap: Map<string, Employee> | null = null;

    constructor(fireApp: FirebaseApp) {
        super(fireApp);
        this.subscribedAssignments = [];
        this.setTargetDate(this.today);
        dateTime.setTargetDate(this.today);
    }

    /**
     * @returns {string}
     */
    getScriptVersion(): string {
        return getLocalStorageWithExpiry("scriptVersion");
    }

    setScriptVersion(version: object) {
        setLocalStorageWithExpiry("scriptVersion", version, 1000 * 60 * 60 * 24 * 365);
    }
    /**
     * @param {Date} date
    **/
    setTargetDate(date: Date) {
        this.targetDate = date;
        this.timeWindowFrom = dateTime.getTimeWindowFrom(date);
        this.timeWindowTo = dateTime.getTimeWindowTo(date);
    }

    /**
     * @returns {CalendarWeek[]}
     */
    getCalendarWeeks(from = this.timeWindowFrom, to = this.timeWindowTo): CalendarWeek[] {
        return dateTime.getCalendarWeeks(from, to);
    }

    /**
     * pre load data
     * @returns {Promise}
     */
    async preLoadData() {
        this.currentUser = (window as any).context.user;
        if (this.hasBackend) {
            //preloading data, async
            //this.getAssignmentsWithCalculatedEmployeesWeeklyHours();
            //this.getSubscribedAssignments();
            this.getAbsences();
            this.getProjects();
            this.getConfig();
            this.getCustomers();
        } else {
            //load mock data
            let seniorities = getLocalStorageWithExpiry("seniorities");

            //generate mock data
            if (seniorities === null) {
                this.loadMockData();
            }
        }
    }


    loadMockData() {
        loadMockData();
    }

    /**
     * @param {{ assignments: AssignmentDemand[] }} assignmentContainer
     */
    calculateTotalWeekly(assignmentContainer: AssignmentDemand, startDate = this.timeWindowFrom, endDate = this.timeWindowTo) {
        var cwWeeks = dateTime.getCalendarWeeks(startDate, endDate);
        cwWeeks.forEach((cw) => {
            var total = 0;
            if (assignmentContainer.assignments) {
                assignmentContainer.assignments.forEach((projectAssigment: AssignmentDemand) => {
                    if (projectAssigment.role === AssignmentDemandRole.SuggestionsAssignmentRole)
                        this.calculateTotalWeekly(projectAssigment, startDate, endDate);
                    else
                        total += projectAssigment.plannedWeeks[cw.key] || 0;
                });
            }
            assignmentContainer.plannedWeeks[cw.key] = total;
        });
    }

    /**
     * Calculate the virtual utilization of an employee
     * based on the employment period start and end date
     * @param {AssignmentDemand} assignment The assignment
     * @param {Employee} employee The employee
     */
    calculateVirtualUitilization(assignment: AssignmentDemand, employee: Employee): void {
        employee.employmentPeriods.filter(period => {
            return period.startDate !== null && period.endDate !== null && assignment.startDate !== null && assignment.endDate !== null
                && period.startDate <= assignment.endDate && period.endDate >= assignment.startDate;
        });
    }
    getMondayDate(date: Date): Date {
        return dateTime.getMondayDate(date);
    }
    
    getFridayDate(date: Date): Date {
        return dateTime.getFridayDate(date);
    }
    
    /**
    * Calculate the weekly hours for multiple assignments.
    * @param {AssignmentDemand} assignment - The assignment to calculate weekly hours for.
    * @param {Date} [from=this.timeWindowFrom] - The start date of the time window.
    * @param {Date} [to=this.timeWindowTo] - The end date of the time window.
    * @returns {Promise<AssignmentDemand>} - The assignment with calculated weekly hours.
    */
    async calculateAssignmentsWeeklyHours(assignments: AssignmentDemand[], from = this.timeWindowFrom, to = this.timeWindowTo): Promise<AssignmentDemand[]> {
        let result = [] as AssignmentDemand[];
        const calendarWeeks = this.getCalendarWeeks(from, to);

        for (const assignment of assignments) {
            let calculatedAssignment = await this.calculateWeeklyBasedOnTotalPlanned(assignment, calendarWeeks);
            result.push(calculatedAssignment);
        }
        return result;
    }

    /**
    * Calculate the weekly hours for a single assignment.
    * @param {AssignmentDemand} assignment - The assignment to calculate weekly hours for.
    * @param {Date} [from=this.timeWindowFrom] - The start date of the time window.
    * @param {Date} [to=this.timeWindowTo] - The end date of the time window.
    * @returns {Promise<AssignmentDemand>} - The assignment with calculated weekly hours.
    */
    async calculateAssignmentWeeklyHours(
        assignment: AssignmentDemand,
        from = this.timeWindowFrom,
        to = this.timeWindowTo
    ): Promise<AssignmentDemand> {
        const calendarWeeks = this.getCalendarWeeks(from, to);
        return await this.calculateWeeklyBasedOnTotalPlanned(assignment, calendarWeeks);
    }

    async calculateWeeklyBasedOnTotalPlanned(assignment: AssignmentDemand, calendarWeeks: CalendarWeek[]): Promise<AssignmentDemand> {
        if (!assignment.startDate || !assignment.endDate) return assignment;
    
        const workingHoursPerDay = 40 / Employee.WorkingDaysInAWeek;
        const total = Math.floor(assignment.pdTotalPlanned * workingHoursPerDay);
    
        const totalWorkingDays = this.getWorkingDaysCount(assignment.startDate, assignment.endDate);
        const dailyDistribution = total / totalWorkingDays;
    
        const assignmentCalendarWeeks = this.getCalendarWeeks(
            this.getMondayDate(assignment.startDate),
            this.getFridayDate(assignment.endDate)
        );
    
        // Combine and deduplicate calendar weeks
        const allCalendarWeeks = new Map<string, CalendarWeek>();
        [...calendarWeeks, ...assignmentCalendarWeeks].forEach((cw) => {
            allCalendarWeeks.set(cw.key, cw);
        });
    
        // Process each calendar week
        for (const cw of allCalendarWeeks.values()) {
            if (assignment.manuallyPlanned) {
                // Set manually planned weeks to 0 if not already set
                assignment.plannedWeeks[cw.key] = assignment.plannedWeeks[cw.key] || 0;
            } else if (
                assignment.startDate <= cw.cw_start &&
                assignment.endDate >= cw.cw_end
            ) {
                // Full week or single week
                assignment.plannedWeeks[cw.key] = Math.round(dailyDistribution * Employee.WorkingDaysInAWeek);
            } else if (assignment.startDate && (assignment.startDate >= cw.cw_start && assignment.startDate <= cw.cw_end) ||
                (assignment.endDate && assignment.endDate >= cw.cw_start && assignment.endDate <= cw.cw_end)) {
                //if the assignment starts or ends during the week, distribute the total planned over the days
                const startDate = assignment.startDate && assignment.startDate > cw.cw_start
                    ? assignment.startDate
                    : cw.cw_start;

                const endDate = assignment.endDate && assignment.endDate < cw.cw_end
                    ? assignment.endDate
                    : cw.cw_end;

                const diffDays = this.getWorkingDaysCount(startDate, endDate); // Correct working day count
                assignment.plannedWeeks[cw.key] = Math.round(diffDays * dailyDistribution);
            } else {
                // Outside the range
                assignment.plannedWeeks[cw.key] = 0;
            }
        }

        return assignment;
    }

    /**
     * @param {Date} startDate 
     * @param {Date} endDate 
     * @returns {number}
     */
    getWorkingDaysCount(startDate: Date, endDate: Date): number {
        return dateTime.getWorkingDaysCount(startDate, endDate);
    }

    async getConfig() {
        return await this.getBackendData("config");
    }

    /**
     * Get locations
     * @returns {Promise<string[]>}
     */
    async getLocations(): Promise<string[]> {
        const config = await this.getConfig();
        const locationsConfig = config.find((c: { id: string; }) => c.id === "locations");
        const values = locationsConfig ? locationsConfig.values : {};
        return Object.keys(values).map((key) => values[key]);
    }

    /**
     * Get seniorities
     * @returns {Promise<string[]>}
     */
    async getSeniorities(): Promise<string[]> {
        const config = await this.getConfig();
        const senioritiesConfig = config.find((c: { id: string; }) => c.id === "seniorities");
        const values = senioritiesConfig ? senioritiesConfig.values : {};
        return Object.keys(values).map((key) => values[key]).sort();
    }

    /**
     * Get customers
     * @returns {Promise<string[]>}
     */
    async getCustomers(): Promise<string[]> {
        if (!this.hasBackend) return [];

        const transformLoad = (data: any[]) => {
            const customers = data.map((d) => d.name);
            return _.orderBy(customers, [(c: string) => c.toLowerCase()]);
        };
        return this.getBackendData("customers", transformLoad, 60 * 60 * 1000);
    }

    /**
     * Get skill structure
     * @returns {Promise<object>}
     */
    async getSkillStructure(): Promise<{ [key: string]: string[] }> {
        const transformLoad = (data: any[]) => {
            const skillStructure: { [key: string]: string[] } = {};
            data.forEach(skill => {
                skillStructure[skill.category] = skill.skills || [];
            });
            return Object.fromEntries(
                Object.entries(skillStructure)
                    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                    .map(([key, values]) => [key, values.sort()])
            );
        };
        return this.getBackendData("skillCategories", transformLoad, 60 * 60 * 1000);
    }

    async getIsoLanguageOptions(): Promise<{ label: string; name: string; value: string; }[]> {
        return iso6391.getAllCodes().map((code) => ({
        label: `${iso6391.getName(code)} (${code})`,
        name: iso6391.getName(code),
        value: code,
        }));
    };

    /**
     * Get employee by id
     * @param {string} employeeId
     * @returns {Promise<Employee>}
     */
    async getEmployee(employeeId: string): Promise<Employee | null> {
        if (!employeeId) return null;
        const employees = await this.getAllEmployeesMap();
        return employees ? employees.get(employeeId) ?? null : null;
    }

    /**
     * get employees
     * @returns {Promise<Employee[]>}
     */
    async getEmployees(): Promise<Employee[]> {
        const transformLoad = (data: any[]) => {
            return data
                .filter((employee: { id: any; }) => Boolean(employee.id))
                .map((employee: any) => new Employee(this.prepareDataForRead(employee)));
        };
        return this.getBackendData("employees", transformLoad, 10 * 60 * 1000);
    }

    /**
     * Get all employees (including internal employees)
     * @returns {Promise<Employee[]>}
     */
    async getAllEmployees(): Promise<Employee[]> {
        const employees = await this.getEmployees();
        const employeeUtilizations = await this.getEmployeesUtilization();
        (employeeUtilizations).forEach(employeeUtilization => {
            var employeeWithChangedWorkingTime = employees.filter(employee => employee.id === employeeUtilization.id);
            if (employeeWithChangedWorkingTime && employeeWithChangedWorkingTime.length > 0) {
                var employee = employeeWithChangedWorkingTime[0];
                let endOfEmployment = employee.employmentPeriods.length > 0 ? employee.employmentPeriods[employee.employmentPeriods.length - 1].endDate : null;
                employee.employmentPeriods = [];
                employeeUtilization.utilization = employeeUtilization.utilization.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
                for (var i = 0; i < employeeUtilization.utilization.length; i++) {
                    let currentUtilization = employeeUtilization.utilization[i];
                    let futureUtilization = employeeUtilization.utilization[i + 1]
                    employee.employmentPeriods.push(new EmploymentPeriod(currentUtilization.startDate, futureUtilization ? futureUtilization.startDate : endOfEmployment, currentUtilization.weeklyHours));
                }
                employee.weeklyHours = employee.getWeeklyHours();
            }
        });

        //const externalEmployees = employees.filter(employee => !employee.role?.startsWith("R-Internal") || employee.role == "R-InternalTrainee");
        const allEmployees = employees;
        this.allEmployeesMap = new Map(allEmployees.map(employee => [employee.id, employee]));
        allEmployees.forEach(employee => {
            if (employee.role?.startsWith("R-Internal")) { employee.role = "(Internal) " + employee.role.slice(10) };
        })
        return allEmployees;
    }

    /**
     * @returns {Promise<Map<string, Employee>}
     */
    async getAllEmployeesMap(): Promise<Map<string, Employee>> {
        if (this.allEmployeesMap != null) return this.allEmployeesMap;
        await this.getAllEmployees();
        return this.allEmployeesMap || new Map<string, Employee>();
    }

    /**
     * Get employees utilization
     * @returns {Promise<Map<string, Array>}
    */
    async getEmployeesUtilization(): Promise<EmployeeUtilization[]> {
        const transformLoad = (data: any[]) => {
            return data.map((e) => {
                return {
                    id: e.id,
                    utilization: e.utilization.map((u: { startDate: Timestamp; weeklyHours: any; }) => {
                        return {
                            startDate: new Date(u.startDate.seconds * 1000),
                            weeklyHours: u.weeklyHours
                        };
                    })
                };
            });
        };
        return this.getBackendData("employeesUtilization", transformLoad, 10 * 60 * 1000);
    }

    /**
     * Get project by id
     * @param {string} projectId 
     * @returns {Promise<Project>}
     */
    async getProject(projectId: string): Promise<Project | null> {
        if (!projectId) return null;
        const projects = await this.getProjects();
        return projects.find((project) => { return project.id === projectId; }) || null;
    }

    /**
     * Get projects
     * @returns {Promise<Project[]>}
     */
    async getProjects(): Promise<Project[]> {
        const transformLoad = (data: any[]) => { return data.map((project) => { return this.prepareDataForRead(project); }); };
        return this.getBackendData("projects", transformLoad, 10 * 60 * 1000);
    }

    /**
     * Get project roles
     * @returns {Promise<string[]>}
     */
    async getProjectRoles(): Promise<string[]> {
        const config = await this.getConfig();
        const values = config.find((c: { id: string; }) => c.id === "projectRoles").values;
        return Object.keys(values).map((key) => values[key]).sort();
    }

    /**
     * Get assignments
     * @returns {Promise<AssignmentDemand[]>}
     */
    async getAssignments(): Promise<AssignmentDemand[]> {
        const transformLoad = (data: any[]) => {
            if (!data) return [];
            data.forEach((assignment) => {
                if (assignment.manuallyPlanned && assignment.manualPlanning) {
                    assignment.manualPlanning.forEach((planning: ManuallyPlannedTime) => {
                        if (assignment.manuallyPlannedWeeks == null) assignment.manuallyPlannedWeeks = [];
                        assignment.manuallyPlannedWeeks[`cw_${planning.week}_${planning.year}`] = planning.hours;
                    });
                }
            });
            return data.map((assignment) => {
                const preparedData = this.prepareDataForRead(assignment);
                return new AssignmentDemand(preparedData, preparedData.manualPlanning);
            });
        };
        return this.getBackendData("assignments", transformLoad);
    }

    private isFetching: boolean = false;
    private eventEmitter = new EventEmitter();

    async clearSubscribedAssignments() {
        this.subscribedAssignments = [];
    }

    async getSubscribedAssignments(): Promise<AssignmentDemand[]> {
        if (window.isTest) {
            return this.getAssignments();
        }

        if (this.isFetching) {
            await new Promise<void>((resolve) => {
                this.eventEmitter.once('fetchingComplete', resolve);
            });
        }

        if (this.subscribedAssignments.length === 0 && !this.isFetching) {
            this.isFetching = true;
            await this.getBackendSubscribedData("assignments", (data: any[]) => {
                data.forEach(element => {
                    let assignment = element.change;
                    if (element.type == "added") {
                        if (assignment.manuallyPlanned && assignment.manualPlanning) {
                            assignment.manualPlanning.forEach((planning: ManuallyPlannedTime) => {
                                if (assignment.manuallyPlannedWeeks == null) assignment.manuallyPlannedWeeks = [];
                                assignment.manuallyPlannedWeeks[`cw_${planning.week}_${planning.year}`] = planning.hours;
                            });
                        }
                        const preparedData = this.prepareDataForRead(assignment);
                        this.subscribedAssignments.push(new AssignmentDemand(preparedData, preparedData.manualPlanning));
                    }
                    else if (element.type == "modified") {
                        let index = this.subscribedAssignments.findIndex((a) => a.id === assignment.id);
                        if (index > -1) {
                            if (assignment.manuallyPlanned && assignment.manualPlanning) {
                                assignment.manualPlanning.forEach((planning: ManuallyPlannedTime) => {
                                    if (assignment.manuallyPlannedWeeks == null) assignment.manuallyPlannedWeeks = [];
                                    assignment.manuallyPlannedWeeks[`cw_${planning.week}_${planning.year}`] = planning.hours;
                                });
                            }
                            const preparedData = this.prepareDataForRead(assignment);
                            this.subscribedAssignments[index] = new AssignmentDemand(preparedData, preparedData.manualPlanning);
                        }
                    }
                    else if (element.type == "removed") {
                        let index = this.subscribedAssignments.findIndex((a) => a.id === assignment.id);
                        if (index > -1) {
                            this.subscribedAssignments.splice(index, 1);
                        }
                    }
                    const preparedData = this.prepareDataForRead(assignment);
                    this.notifySubscribedAssignments(new AssignmentDemand(preparedData, preparedData.manualPlanning), element.type);
                });
            });
            this.isFetching = false;
            this.eventEmitter.emit('fetchingComplete');
        }

        return this.subscribedAssignments;
    }

    assignmentChangeSubscribe(callback: (assignment: AssignmentDemand, type: string) => void): void {
        this.assignmentChangeSubscribers.push(callback);
    }

    assignmentChangeUnsubscribe(callback: (assignment: AssignmentDemand, type: string) => void): void {
        this.assignmentChangeSubscribers = this.assignmentChangeSubscribers.filter(subscriber => subscriber !== callback);
    }

    assignmentChangeUnsubscribeAll(): void {
        this.assignmentChangeSubscribers = [];
    }

    private notifySubscribedAssignments(assignment: AssignmentDemand, type: string): void {
        this.assignmentChangeSubscribers.forEach(subscriber => {
            subscriber(assignment, type);
        });
    }

    /**
     * Get assignments with calculated employee weekly working hours based on 40h per week
     * @returns {Promise<AssignmentDemand[]>}
    */
     async getAssignmentsWithCalculatedEmployeesWeeklyHours(): Promise<AssignmentDemand[]> {
        const assignments = await this.getSubscribedAssignments();
        const assignmentsMap = await this.calculateAssignmentsWeeklyHours(assignments);
        return assignmentsMap;
    }

    /**
     * Get assignment by id
     * @returns {Promise<AssignmentDemand>}
     */
    async getAssignment(assignmentId: string): Promise<AssignmentDemand | null> {
        if (!assignmentId) return null;
        const preparedData = this.prepareDataForRead(await this.getEntity("assignments", assignmentId));
        const assignment = new AssignmentDemand(preparedData, preparedData.manualPlanning);
        return this.calculateAssignmentWeeklyHours(assignment);
    }

    /**
     * Get assignment realtime with calculated employee weekly working hours based on employement contract
     * @returns {Function} unsubscribe function
     */
    getAssignmentRealtime(assignmentId: string, callback: (arg0: AssignmentDemand) => void) {
        if (!assignmentId) return null;
        const transformLoadCallback = async (data: any) => {
            const preparedData = this.prepareDataForRead(data);
            if (preparedData === undefined) return;
            const assignment = new AssignmentDemand(preparedData, preparedData.manualPlanning);
            callback(await this.calculateAssignmentWeeklyHours(assignment));
        }
        return this.getEntityRealtime("assignments", assignmentId, transformLoadCallback);
    }

    /**
     * Get absences
     * @returns {Promise<Absence[]>}
     */
    async getAbsences(): Promise<Absence[]> {
        if (!this.hasBackend) return [];
        const transformLoad = (data: any[]) => {
            let result: Absence[] = [];
            data.forEach((employee) => {
                let abscences = employee.absences.map((absence: Absence) => {
                    return new Absence(employee.employeeId, absence.startDate, absence.endDate, absence.fullDay);
                });
                result.push(...abscences);
            });

            return result;
        };
        return this.getBackendData("absences", transformLoad);
    }

    /**
     * Get status
     * @returns {Promise<string[]>}
     */
    async getStatus() {
        return AssignmentDemand.AllDemandStatuses;
    }

    /**
     * Get skills for a given description using AI
     * @param {string} input The description
     * @returns {Promise<{Skill[],Language[]}>}
     */
    async getMagicSkills(input: string) {
        const url = 'https://aiskills-wygx2zecha-ey.a.run.app';
        let idToken = '';
        if (this.currentUser?.firebaseUser) {
            try {
                idToken = await this.currentUser.firebaseUser.getIdToken();
            } catch (error) {
                console.error('Failed to get ID token:', error);
            }
        }
        try {
            const result = await this.fetchAIResults(url, input, idToken);
            const skillStructure = await this.getSkillStructure();
            const skills = (JSON.parse((result as any).text).skills as Skill[]).filter((skill: Skill) => skillStructure && skillStructure[skill.category]);
            const languages = (JSON.parse((result as any).text).languages as Language[]);
            return { skills, languages };
        } catch (error) {
            throw error;
        }
    }

    /** 
     * Get demands for a given description using AI
     * @param {string} input The description
     * @returns {Promise<object[]>}
     */
    async getMagicDemands(input: string): Promise<AssignmentDemand[]> {
        const url = 'https://aidemands-wygx2zecha-ey.a.run.app';
        let idToken = '';
        if (this.currentUser?.firebaseUser) {
            try {
                idToken = await this.currentUser.firebaseUser.getIdToken();
            } catch (error) {
                console.error('Failed to get ID token:', error);
            }
        }
        try {
            const result = await this.fetchAIResults(url, input, idToken);
            const assignments = (result as unknown as AssignmentDemand[]);
            return assignments;
        } catch (error) {
            throw error;
        }
    }

    async fetchAIResults(url: string, input: string, idToken: string): Promise<AssignmentDemand> {
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json',
                            'Authorization': `Bearer ${idToken}`
                          },
                body: JSON.stringify({ data: input })
            });

            if (!response.ok) throw new Error('Network response was not ok');

            const responseText = await response.text();
            const fixedJsonString = responseText.replace(/}\s*{/g, '},{').replace(/"(\s*)"/g, '","');
            const parsedResult = JSON.parse(fixedJsonString);

            return parsedResult;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Get absences Map
     * @returns {Promise<Map<string, Absence[]>>}
     */
    async getAbsencesMapByEmployeeId() {
        const absences = await this.getAbsences();

        /** @type {Map<string, Absence[]>}  */
        const absencesMap = new Map();
        absences.forEach(absence => {
            const employeeAbsences = absencesMap.get(absence.employeeId);
            const newEmployeeAbsences = employeeAbsences == undefined
                ? [absence]
                : [...employeeAbsences, absence]
            absencesMap.set(absence.employeeId, newEmployeeAbsences);
        });

        return absencesMap;
    }

    /**
     * get Employee Assignments
     * only employees with employment period in the time window
     * @param {boolean} isCalculating is the data being used to calculate total/average (to exclude internal employees)
     * @returns {Promise<Array>} An array of employee assignments
     */
    async getEmployeeAssignments(isCalculating: boolean = false, assignmentsPromise: Promise<AssignmentDemand[]> = this.getAssignmentsWithCalculatedEmployeesWeeklyHours()): Promise<AssignmentDemand[]> {
        const [employees, absencesMap, employeesUtilization] = await Promise.all([this.getAllEmployees(), this.getAbsencesMapByEmployeeId(), this.getEmployeesUtilization()]);
        
        let assignments = await assignmentsPromise;

        let suggestedAssignments = assignments.filter((x) => { return x.status != "Dropped" && x.status != "Lost" && x.status != "Resolved" && x.suggestions.length > 0 });

        assignments = assignments.filter((assignment) => { return assignment.status == "Resolved" })
            .filter((assignment) => {
                //assignment time frame is overlapping with the time window
                return assignment.startDate && assignment.endDate && assignment.startDate <= this.timeWindowTo && assignment.endDate >= this.timeWindowFrom;
            })

        const calendarWeeks = dateTime.getCalendarWeeks(this.timeWindowFrom, this.timeWindowTo);

        let validEmployees = employees
            .filter((employee) => { return employee.employmentPeriods.length > 0; })
            .filter((employee) => {
                var endDate = employee.employmentPeriods[employee.employmentPeriods.length - 1].endDate;
                return endDate === null || endDate >= this.timeWindowFrom;
            })
        //.filter((employee) => { return !employee.leads.includes("Lead-EX"); })
        if (isCalculating) {
            validEmployees = validEmployees.filter((employee) => { return employee.role ? !employee.role.startsWith("(Internal) ") : true; })
        }

        let employeeAssignments: AssignmentDemand[] = validEmployees.map((employee) => {
            const assignmentContainer = new AssignmentDemand({
                role: employee.employeeName, //employee.firstName + " " + employee.lastName,
                startDate: this.timeWindowFrom,
                endDate: this.timeWindowTo,
                assignments: assignments.filter((x) => {
                    return x.employeeId == employee.id;
                }),
                employee: employee,
            }
            );
            if (assignmentContainer.assignments) {
                assignmentContainer.assignments.push(
                    this.createAbsenceAssigment(employee, calendarWeeks, absencesMap.get(employee.id), employeesUtilization.filter(data => data.id == employee.id)));
                let suggestedAssignmentsContainer = this.createSuggestionsAssigment(employee, suggestedAssignments);
                if (suggestedAssignmentsContainer.assignments && suggestedAssignmentsContainer.assignments.length > 0)
                    assignmentContainer.assignments.push(suggestedAssignmentsContainer);
            }
            this.calculateTotalWeekly(assignmentContainer);

            let numberOfOriginatingProjects = 0;
            numberOfOriginatingProjects = assignmentContainer.assignments?.reduce(function (_acc, k) {
                if (k.originatingProjectId !== '' && k.originatingProjectId !== undefined) {
                    _acc++;
                }
                return _acc;
            }, 0) || 0;
            if (numberOfOriginatingProjects > 0)
                assignmentContainer.originatingProjectId = 'Copied demands (' + numberOfOriginatingProjects + ')';
            else
                assignmentContainer.originatingProjectId = "";

            this.calculateVirtualUitilization(assignmentContainer, employee);

            return assignmentContainer;
        });

        return employeeAssignments;
    }

    async getEmployeeAssignmentsByEmployeeId(employeeId: string): Promise<AssignmentDemand | null> {
        const employee = await this.getEmployee(employeeId);
        if (employee == undefined) return null;

        const absencesMap = await this.getAbsencesMapByEmployeeId();
        const assignments = await this.getSubscribedAssignments();
        const employeeAssignments = assignments.filter(x => x.employeeId == employeeId && x.status == "Resolved");

        const dates = this.getAssignmentDates(employeeAssignments);

        if (dates.length === 0)
            return new AssignmentDemand({ role: employee.employeeName, assignments: employeeAssignments, employee: employee });

        const minDate = new Date(Math.min(...dates.map(date => date.getTime())));
        const maxDate = new Date(Math.max(...dates.map(date => date.getTime())));

        const assignmentContainer = new AssignmentDemand({
            role: employee.employeeName,
            startDate: minDate,
            endDate: maxDate,
            assignments: employeeAssignments,
            employee: employee,
        }
        );

        let calendarWeeks = dateTime.getCalendarWeeks(minDate, maxDate);
        if (assignmentContainer.assignments)
            assignmentContainer.assignments.push(
                this.createAbsenceAssigment(employee, calendarWeeks, absencesMap.get(employee.id)));
        this.calculateTotalWeekly(assignmentContainer, minDate, maxDate);
        return assignmentContainer;
    }

  private getAssignmentDates(employeeAssignments: AssignmentDemand[]): Date[] {
        return employeeAssignments.flatMap(x => {
            if (x.manuallyPlanned) {
                let startWeek = 0;
                let startYear = 0;
                let endWeek = 0;
                let endYear = 0;

                const plannedWeekKeys = Object.keys(x.plannedWeeks).filter(weekKey => x.plannedWeeks[weekKey] > 0);

                if (plannedWeekKeys.length > 0) {
                    plannedWeekKeys.forEach(weekKey => {
                        const [_, week, year] = weekKey.split('_');
                        const weekNumber = parseInt(week);
                        const yearNumber = parseInt(year);

                        if (startYear === 0 || (yearNumber < startYear || (yearNumber === startYear && weekNumber < startWeek))) {
                            startYear = yearNumber;
                            startWeek = weekNumber;
                        }
                        if (yearNumber > endYear || (yearNumber === endYear && weekNumber > endWeek)) {
                            endYear = yearNumber;
                            endWeek = weekNumber;
                        }
                    });
                }

                let startDate = startYear > 0 ? this.getDateFromWeek(startWeek, startYear) : new Date();
                let endDate = endYear > 0 ? this.getDateFromWeek(endWeek, endYear) : new Date();
                return [startDate, endDate];
            }
            return [x.startDate, x.endDate]
        })
            .filter(x => x !== null)
            .map(x => new Date(x!));
    }

    getDateFromWeek(week: number, year: number): Date {
        const jan4 = new Date(year, 0, 4);
        const dayOfWeek = jan4.getDay() || 7;
        const mondayOfFirstWeek = new Date(jan4);
        mondayOfFirstWeek.setDate(jan4.getDate() - (dayOfWeek - 1));
        const targetDate = new Date(mondayOfFirstWeek);
        targetDate.setDate(mondayOfFirstWeek.getDate() + (week - 1) * 7);
        return targetDate;
    }

    createSuggestionsAssigment(employee: Employee, assignments: AssignmentDemand[] = []): AssignmentDemand {
        const suggestedAssignments = new AssignmentDemand({
            role: AssignmentDemandRole.SuggestionsAssignmentRole,
            id: employee.id,
            employeeId: employee.id,
            startDate: this.timeWindowFrom,
            endDate: this.timeWindowTo,
            employee: employee
        });
        suggestedAssignments.assignments = assignments.filter((x) => {
            return x.suggestions && x.suggestions.includes(employee.id);
        });
        return suggestedAssignments;
    }




    /**
     * @param {Employee} employee
     * @param {CalendarWeek[]} calendarWeeks
     * @param {Absence[]} employeeAbsences
     * @returns {AssignmentDemand}
     */
    createAbsenceAssigment(employee: Employee, calendarWeeks: CalendarWeek[], employeeAbsences: Absence[] = [], employeesUtilization: EmployeeUtilization[] = []) {

        /**
         * @param {number} numberOfWorkingDays
         * @returns {number}
         */
        function calculateAbsenceHours(numberOfWorkingDays: number, workingHoursInWeek: number): number {
            return workingHoursInWeek - Employee.calculateWorkingHoursInWeek(employee, numberOfWorkingDays, workingHoursInWeek);
        }

        const absenceAssignment = new AssignmentDemand({ role: AssignmentDemandRole.AbsenceAssigmentRole, id: employee.id, employeeId: employee.id });

        calendarWeeks.forEach(cw => {
            let absenceHoursInWeek = employee.weeklyHours;
            let utilization = employeesUtilization.filter(data => data.id == employee.id);
            let weeklyUtilisationHours = utilization.length > 0 ? getEmployeeUtilizationByWeek(utilization[0].utilization, cw.cw, cw.year) : 40;
            let employeeEndDate = employee.employmentPeriods[employee.employmentPeriods.length - 1].endDate ? employee.employmentPeriods[employee.employmentPeriods.length - 1].endDate : undefined;
            let employeeStartDate = employee.employmentPeriods[0].startDate ? employee.employmentPeriods[0].startDate : undefined;

            if (employeeEndDate && employeeEndDate < cw.cw_start) {
                absenceAssignment.plannedWeeks[cw.key] = absenceHoursInWeek;
                return;
            }
            if (cw.cw_start < employeeStartDate!) {
                absenceAssignment.plannedWeeks[cw.key] = absenceHoursInWeek;
                return;
            }

            /**@type {Date[]} */
            let employmentDaysInWeek: Date[] = [];

            const daysOfWeek = cw.getDaysOfWeek();

            // Calculate absence hours based on Employment periods
            const relevantEmploymentPeriods = employee.employmentPeriods
                .filter(period =>
                    (period.startDate && period.startDate <= cw.cw_start && (period.endDate === null || cw.cw_end <= period.endDate)) ||
                    (period.startDate && cw.cw_start <= period.startDate && period.startDate <= cw.cw_end) ||
                    (period.endDate && cw.cw_start <= period.endDate && period.endDate <= cw.cw_end)
                )
                .map(period => {
                    const overlapDaysWithCalendarWeek = daysOfWeek.filter(day => period.isInEmploymentPeriod(day));
                    return {
                        period: period,
                        overlapDays: overlapDaysWithCalendarWeek
                    }
                });

            if (relevantEmploymentPeriods.length === 1) {
                absenceHoursInWeek = calculateAbsenceHours(relevantEmploymentPeriods[0].overlapDays.length, weeklyUtilisationHours);
                employmentDaysInWeek = relevantEmploymentPeriods[0].overlapDays;
            } else if (relevantEmploymentPeriods.length !== 0) {
                /** @type {Map<number, undefined>} */
                const distinctOverlapDaysMap: Map<number, undefined> = new Map();
                relevantEmploymentPeriods.flatMap(period => period.overlapDays).forEach((day: Date) => {
                    const time = day.getTime();
                    if (!distinctOverlapDaysMap.has(time)) distinctOverlapDaysMap.set(time, undefined);
                })

                absenceHoursInWeek = calculateAbsenceHours(distinctOverlapDaysMap.size, weeklyUtilisationHours);
                employmentDaysInWeek = Array.from(distinctOverlapDaysMap.keys()).map(time => new Date(time));
            }


            // Calculate absence hours and working days based on Absence days
            const relevantAbsences = employeeAbsences
                .filter(absence =>
                    (absence.startDate && new Date(absence.startDate.seconds * 1000) <= cw.cw_start && (absence.endDate == null || cw.cw_end <= new Date(absence.endDate.seconds * 1000))) ||
                    (absence.startDate && cw.cw_start <= new Date(absence.startDate.seconds * 1000) && new Date(absence.startDate.seconds * 1000) <= cw.cw_end) ||
                    (absence.startDate && absence.endDate && cw.cw_start <= new Date(absence.endDate.seconds * 1000) && new Date(absence.endDate.seconds * 1000) <= cw.cw_end)
                )
                .map(absence => {
                    function stripTime(date: Date) {
                        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
                    }

                    const overlapDaysWithCalendarWeek = daysOfWeek.filter(day =>
                        absence.startDate && stripTime(new Date(absence.startDate.seconds * 1000)) <= stripTime(day) && absence.endDate &&
                        (stripTime(day) <= stripTime(new Date(absence.endDate.seconds * 1000)) || absence.endDate == null)
                    );

                    return {
                        absence: absence,
                        overlapDays: overlapDaysWithCalendarWeek
                    }
                });

            // If not in employment periods and have no absence days => accept prev result
            if (absenceHoursInWeek === employee.weeklyHours || relevantAbsences.length === 0) {
                absenceAssignment.plannedWeeks[cw.key] = absenceHoursInWeek;
                return;
            }

            // Calculate absence hours based on Absence days

            /** @type {Map<number, Absence>} */
            const absenceDaysMap = new Map();
            relevantAbsences.forEach(({ absence, overlapDays }) => {
                overlapDays.forEach(day => {
                    if (!absenceDaysMap.has(day.getTime())) {
                        absenceDaysMap.set(day.getTime(), absence);
                    }
                })
            });
            const overlapEmploymentAndAbsenceDays = _.intersection(relevantAbsences.flatMap(a => a.overlapDays), employmentDaysInWeek);
            const absenceDaysCount = overlapEmploymentAndAbsenceDays.reduce(
                (absenceDaysCount, absenceDay) => {
                    const absenceDayInfo = absenceDaysMap.get(absenceDay.getTime());
                    return absenceDayInfo != undefined
                        ? absenceDaysCount + (absenceDayInfo.fullDay ? 1 : 0.5)
                        : absenceDaysCount;
                }, 0);
            const totalWorkingDaysInWeek = employmentDaysInWeek.length - absenceDaysCount;
            absenceAssignment.plannedWeeks[cw.key] = calculateAbsenceHours(totalWorkingDaysInWeek, weeklyUtilisationHours);
        });

        return absenceAssignment;
    }

    /**
     * get Project assignments
     * @returns {ProjectAssignment[]} An array of project assignments
     */
    async getProjectAssignments() {
        let assignments = await this.getAssignmentsWithCalculatedEmployeesWeeklyHours();
        assignments = assignments.filter((assignment) => { return assignment.status == "Open" || assignment.status == "Resolved" })
            .filter((assignment) => {
                //assignment time frame is overlapping with the time window
                return assignment.startDate && assignment.endDate && assignment.startDate <= this.timeWindowTo && assignment.endDate >= this.timeWindowFrom;
            });
        const assignmentProjects = await this.getAssignmentProjects(assignments);
        //handle other or unnamed projects
        const otherProject: Project = new Project({
            id: "",
            projectName: "Unassigned",
        });
        const allProjects = [otherProject, ...assignmentProjects];

        let foundAssignments: string[] = [];

        const projectAssignments = allProjects.map((project) => {
            const assignmentContainer = new AssignmentDemand({
                role: project.projectName ? project.id + " - " + project.projectName : project.id,
                startDate: project.startDate ? project.startDate : this.timeWindowFrom,
                endDate: project.endDate ? project.endDate : this.timeWindowTo,
                assignments: assignments.filter((x) => {
                    if ((x.projectId ? x.projectId : "").replace(/\s+/g, ' ').trim() === project.id) {
                        foundAssignments.push(x.id);
                        return true;
                    }
                    return false;
                }),
                project: project,
            });

            let numberOfOriginatingProjects = 0;
            numberOfOriginatingProjects = assignmentContainer.assignments?.reduce(function (_acc, k) {
                if (k.originatingProjectId !== '') {
                    _acc++;
                }
                return _acc;
            }, 0) || 0;
            if (numberOfOriginatingProjects > 0)
                assignmentContainer.originatingProjectId = 'Copied demands (' + numberOfOriginatingProjects + ')';
            else
                assignmentContainer.originatingProjectId = "";
            this.calculateTotalWeekly(assignmentContainer);
            return assignmentContainer;
        });

        //handle orphan assignments
        const orphanAssignments = assignments.filter((x) => {
            return !foundAssignments.includes(x.id);
        });

        if (orphanAssignments.length > 0) {
            const assignmentContainer = new AssignmentDemand({
                role: " - Orphan Assignments",
                startDate: this.timeWindowFrom,
                endDate: this.timeWindowTo,
                assignments: orphanAssignments,
                project: otherProject
            });
            this.calculateTotalWeekly(assignmentContainer);
            projectAssignments.push(assignmentContainer);
        }

        return projectAssignments;
    }

    /**
     * get Project assignments
     * @param {AssignmentDemand[]} assignments An array of assignments
     * @returns {Project[]} An array of project assignments
    */
    async getAssignmentProjects(assignments: AssignmentDemand[]): Promise<Project[]> {
        const projectIds = [...new Set(assignments
            .map(x => x.projectId ? x.projectId.replace(/\s+/g, ' ').trim() : "")
            .filter(projectId => projectId != "" && projectId != null))];

        const projects = await this.getProjects();
        let excludedProjects = projects.filter((project) => { return !projectIds.includes(project.id); });
        let combinedProjects = [...projectIds, ...excludedProjects.map(x => x.id)];
        let result = combinedProjects.map((projectId) => {
            let project = projects.find(x => x.id == projectId);
            return new Project({
                id: projectId,
                projectName: project?.projectName,
                lead_0: project?.lead_0,
                lead_1: project?.lead_1,
                lead_2: project?.lead_2,
                lead_3: project?.lead_3,
                project_status_id: project?.project_status_id,
                lead_4: project?.lead_4,
                startDate: project ? project.startDate : this.timeWindowFrom,
                endDate: project ? project.endDate : this.timeWindowTo
            })
        });

        return result;
    }

    /**
     * Update or Insert Employee Assignments
     * @param {AssignmentDemand} assignment An employee assignment
     * @returns {AssignmentDemand} An employee assignment
     */
    async updateAssignment(assignment: AssignmentDemand): Promise<AssignmentDemand> {
        if (this.hasBackend) {
            assignment.skills.skills = assignment.skills.all();
            const serializable = JSON.parse(JSON.stringify(assignment.toFireStoreObject()));
            this.setEntity("assignments", serializable);
            this.clearStoreLocalStorage("assignments");
            return assignment;
        } else {
            return assignment;
        }
    }

    async updateAssignmentComment(comment: AssignmentComment): Promise<AssignmentComment> {
        if (this.hasBackend) {
            this.getEntity("assignmentComments", comment.assignmentId).then((existingComments) => {
                let allComments: any[] = [];
                if (existingComments && (existingComments as { comments: AssignmentComment[] }).comments) {
                    allComments = (existingComments as { comments: AssignmentComment[] }).comments.map(c => ({ ...c }));
                }
                const plainComment = { ...comment, createdDate: new Date(comment.createdDate).toISOString() };
                allComments.push(plainComment);
                this.setEntity("assignmentComments", { id: comment.assignmentId, comments: allComments });
            });
            this.clearStoreLocalStorage("assignmentComments");
            return comment;
        } else {
            return comment;
        }
    }

    async updateAssignmentHistory(assignmentHistory: AssignmentsHistory) {
        if (this.hasBackend) {
            const serializable = JSON.parse(JSON.stringify(assignmentHistory));
            await this.setEntity("assignmentsHistory", serializable);
            this.clearStoreLocalStorage("assignmentsHistory");
            return assignmentHistory;
        } else {
            return assignmentHistory;
        }
    }

    async getAssignmentsHistory(): Promise<AssignmentsHistory[]> {
        if (this.hasBackend) return await this.getBackendData("assignmentsHistory");
        return [];
    }

    async getAssignmentHistory(assignmentId: string): Promise<AssignmentsHistory | null> {
        if (this.hasBackend) return await this.getEntity("assignmentsHistory", assignmentId) as AssignmentsHistory;
        return null;
    }

    async getAssignmentComments(assignmentId: string): Promise<AssignmentComment[]> {
        if (this.hasBackend && assignmentId) {
            const comments = await this.getEntity("assignmentComments", assignmentId) as { comments: AssignmentComment[] };
            return comments ? comments.comments : [];
        }
        return [];
    }

    async deleteAssignment(assignmentId: string) {
        if (this.hasBackend) {
            await this.deleteEntity("assignments", assignmentId);
            this.clearStoreLocalStorage("assignments");
            return assignmentId;
        } else {
            return assignmentId;
        }
    }

    async deleteAssignmentHistory(assignmentId: string) {
        if (this.hasBackend) {
            await this.deleteEntity("assignmentsHistory", assignmentId);
            this.clearStoreLocalStorage("assignmentsHistory");
            return assignmentId;
        } else {
            return assignmentId;
        }
    }

    async getAssignmentNewComments(): Promise<AssignmentComment[]> {
        const lastReadComments = getLocalStorageWithExpiry("lastReadComments", null, new Date(1970, 1, 1).getTime());
        const assignments = await this.getAssignments();

        //filter assignments that contain new comments
        const assignmentsWithNewComments = assignments.filter((assignment) => {
            return assignment.comments && assignment.comments.length > 0 && assignment.comments.some((comment) => {
                return comment.createdDate > lastReadComments;
            });
        });

        const userId = this.currentUser ? this.currentUser.userId.toLowerCase() : '';
        const regUserId = new RegExp("[\\+|\\@]" + userId, "i");
        //get all comments that are newer than the last read date and contain a mention of the current user
        const comments = assignmentsWithNewComments.map((assignment) => {
            return assignment.comments.filter((comment) => {
                return comment.createdDate > lastReadComments && regUserId.test(comment.message);
            }).map((comment) => {
                return new AssignmentComment({
                    assignmentId: assignment.id,
                    projectId: assignment.projectId,
                    message: comment.message,
                    createdDate: comment.createdDate,
                    createdBy: comment.createdBy,
                })
            });
        }).flat();

        //sort comments by date
        comments.sort((a, b) => {
            return new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime();
        });

        return comments;
    }

    setNewCommentReadDate() {
        setLocalStorageWithExpiry("lastReadComments", new Date(), 1000 * 60 * 60 * 24 * 365);
    }


    /**
     * Call external API to send email
     * @param {string} emailType 
     * @param {Object} data 
     * @returns {void}
     */
    async sendEmail(emailType: string, data: object) {

        if (!this.hasBackend) return;
        let url = "";
        switch (emailType) {
            case "commentEmail":
                url = location.hostname === "assignments.zoi.tech" ?
                    "https://sendmentionedcommentemail-c4btxvac2q-ey.a.run.app" :
                    "https://sendmentionedcommentemail-wygx2zecha-ey.a.run.app"
                break;
            case "newCommentEmail":
                url = location.hostname === "assignments.zoi.tech" ?
                    "https://sendnewcommentemail-c4btxvac2q-ey.a.run.app" :
                    "https://sendnewcommentemail-wygx2zecha-ey.a.run.app";
                break;
            case "assignmentDataChangeEmail":
                url = location.hostname === "assignments.zoi.tech" ?
                    "https://sendchangeddemanddataemails-c4btxvac2q-ey.a.run.app" :
                    "https://sendchangeddemanddataemails-wygx2zecha-ey.a.run.app";
                break;
            default:
                break;
        }

        // Get the ID token from the current user
        let idToken = '';
        if (this.currentUser?.firebaseUser) {
            try {
                idToken = await this.currentUser.firebaseUser.getIdToken();
            } catch (error) {
                console.error('Failed to get ID token:', error);
            }
        }

        await fetch(url, {
            method: 'POST',
            headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ data: data })
        });

        //if (!response.ok) throw new Error('Network response was not ok');
        return;
    }

    async sendMentionedCommentEmail(recipients: string[], comment: string, senderName: string, url: string) {
        const data = { recipients, comment, senderName, url };
        await this.sendEmail("commentEmail", data);
    }

    async sendNewCommentEmail(recipient: string, comment: string, senderName: string, url: string) {
        const data = { to: recipient, comment, senderName, url };
        await this.sendEmail("newCommentEmail", data);
    }

    async sendDataChangeEmail(assignmentId: string, recipient: string, senderName: string, changedData: any, isNew: boolean) {
        const data = { assignmentId: assignmentId, responsible: recipient, senderName: senderName, isNew: isNew, changedDataStr: changedData };
        await this.sendEmail("assignmentDataChangeEmail", data);
    }
}

// Tri Cao was here